const EXPO_PUSH_TOKEN_RE = /^(ExponentPushToken|ExpoPushToken)\[[^\]]+\]$/;

export const isExpoPushToken = (token) =>
    typeof token === 'string' && EXPO_PUSH_TOKEN_RE.test(token.trim());

const normalizePushTokens = (token) => {
    const rawTokens = Array.isArray(token) ? token : [token];
    const validTokens = [];
    const seen = new Set();
    let invalidTokenCount = 0;

    for (const rawToken of rawTokens) {
        const normalizedToken = typeof rawToken === 'string' ? rawToken.trim() : '';
        if (!isExpoPushToken(normalizedToken)) {
            invalidTokenCount += 1;
            continue;
        }

        if (seen.has(normalizedToken)) {
            continue;
        }

        seen.add(normalizedToken);
        validTokens.push(normalizedToken);
    }

    return {
        normalizedToken:
            validTokens.length === 0
                ? null
                : Array.isArray(token)
                  ? validTokens
                  : validTokens[0],
        tokenCount: validTokens.length,
        invalidTokenCount,
    };
};

const asArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    return value == null ? [] : [value];
};

const toFiniteNumber = (value, fallback) =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const hasErrorValue = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    if (value && typeof value === 'object') {
        return Object.keys(value).length > 0;
    }

    if (typeof value === 'string') {
        return value.trim().length > 0;
    }

    return Boolean(value);
};

const getPayloadErrors = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return [];
    }

    return [
        ...asArray(payload.error).filter(hasErrorValue),
        ...asArray(payload.errors).filter(hasErrorValue),
    ];
};

const getExpoDataItems = (payload) => {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (!payload || typeof payload !== 'object') {
        return [];
    }

    if (payload.data != null) {
        return asArray(payload.data);
    }

    if (payload.tickets != null) {
        return asArray(payload.tickets);
    }

    if (payload.ticket != null) {
        return asArray(payload.ticket);
    }

    if (payload.status === 'ok' || payload.status === 'error') {
        return [payload];
    }

    return [];
};

const getLoggedToken = (normalizedToken) =>
    Array.isArray(normalizedToken)
        ? { tokenCount: normalizedToken.length }
        : normalizedToken;

const summarizePushPayload = (payload, fallbackSuccessCount, invalidTokenCount) => {
    const dataItems = getExpoDataItems(payload);
    const payloadErrors = getPayloadErrors(payload);
    const summary = payload?.summary && typeof payload.summary === 'object'
        ? payload.summary
        : null;

    if (dataItems.length === 0) {
        if (summary) {
            const errorCount = toFiniteNumber(
                summary.errorCount,
                payloadErrors.length,
            );

            return {
                ...summary,
                tokenCount: toFiniteNumber(
                    summary.tokenCount,
                    fallbackSuccessCount,
                ),
                successCount: toFiniteNumber(
                    summary.successCount,
                    errorCount > 0 ? 0 : fallbackSuccessCount,
                ),
                errorCount,
                invalidTokenCount: toFiniteNumber(
                    summary.invalidTokenCount,
                    invalidTokenCount,
                ),
            };
        }

        const errorCount = payloadErrors.length;

        return {
            tokenCount: fallbackSuccessCount,
            successCount: errorCount > 0 ? 0 : fallbackSuccessCount,
            errorCount,
            invalidTokenCount,
        };
    }

    const dataErrorCount = dataItems.filter(
        (item) => item?.status === 'error' || getPayloadErrors(item).length > 0,
    ).length;
    const errorCount = dataErrorCount + payloadErrors.length;
    const tokenCount = summary
        ? toFiniteNumber(summary.tokenCount, dataItems.length)
        : dataItems.length;
    const successCount = summary
        ? toFiniteNumber(summary.successCount, dataItems.length - dataErrorCount)
        : dataItems.length - dataErrorCount;

    return {
        ...(summary || {}),
        tokenCount,
        successCount,
        errorCount,
        invalidTokenCount: summary
            ? toFiniteNumber(summary.invalidTokenCount, invalidTokenCount)
            : invalidTokenCount,
    };
};

export const RequestPushMsg = (token, data) => {
    const { normalizedToken, tokenCount, invalidTokenCount } = normalizePushTokens(token);
    const url = 'https://api.tasirimkurye.com/send_notification';
    const logToken = getLoggedToken(normalizedToken);

    if (!normalizedToken) {
        console.warn('[push] Skipping notification because recipient token is invalid.', {
            token: logToken,
            screen: data?.screen,
            params: data?.params,
            title: data?.title,
        });
        return Promise.resolve({
            ok: false,
            skipped: true,
            reason: 'invalid_token',
            successCount: 0,
            errorCount: invalidTokenCount,
            invalidTokenCount,
        });
    }

    return fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            token: normalizedToken,
            ...data,
        }),
    })
        .then(async (response) => {
            let bodyText = '';
            let payload = null;

            try {
                bodyText = await response.text();
            } catch (error) {
            }

            if (bodyText) {
                try {
                    payload = JSON.parse(bodyText);
                } catch (error) {
                }
            }

            const summary = summarizePushPayload(payload, tokenCount, invalidTokenCount);
            const payloadErrors = getPayloadErrors(payload);
            const ok = response.ok && payloadErrors.length === 0 && summary.successCount > 0 && summary.errorCount === 0;

            if (!ok) {
                console.error('[push] Notification request failed.', {
                    status: response.status,
                    statusText: response.statusText,
                    token: logToken,
                    screen: data?.screen,
                    params: data?.params,
                    title: data?.title,
                    body: bodyText,
                    errors: payloadErrors,
                    summary,
                });
            }

            return {
                ok,
                status: response.status,
                body: bodyText,
                payload,
                ...summary,
            };
        })
        .catch((error) => {
            console.error('[push] Notification request crashed.', {
                token: logToken,
                screen: data?.screen,
                params: data?.params,
                title: data?.title,
                error: error?.message || error,
            });
            return {
                ok: false,
                error: error?.message || error,
                successCount: 0,
                errorCount: tokenCount,
                invalidTokenCount,
            };
        });
};
